import { useEffect, useState, useMemo } from "react";
import {
    Modal, Box, Button, Typography, Grid, MenuItem, Backdrop,
    Fade, Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import LoadingButton from "@mui/lab/LoadingButton";
import * as Yup from "yup";
import useFetch from "../../../hooks/useFetch";
import REACT_APP from "../../../environment";
import axios from "axios";
import { toast } from "react-toastify";

// Regex for name validation
const regex = {
    text: /^[a-zA-Z0-9\s\-.,!?()]+$/
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "60%",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
};

export default function TranslationsModal({ amenityGroups, getAmenities }) {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [loading, setLoading] = useState(false);

    const commonHeaders = {
        "client-id": REACT_APP.COMMON_ID,
        secret: REACT_APP.COMMON_SECRET
    };
    const { data: languages = [], fetchData: getLangs } = useFetch(
        `${REACT_APP.COMMON_API}/tts/api/v01/languages`,
        { headers: commonHeaders }
    );

    useEffect(() => {
        getLangs();
    }, []);

    const requiredLangId = 2;

    const initialValues = useMemo(() => ({
        group: "",
        translations: languages?.reduce((acc, lang) => {
            acc[lang.id] = { name: "", comment: "" };
            return acc;
        }, {})
    }), [languages]);

    const validationSchema = useMemo(() => {
        const translationShape = {};
        languages?.forEach((lang) => {
            translationShape[lang.id] = Yup.object().shape({
                name: lang.id === requiredLangId
                    ? Yup.string().required("Required").matches(regex.text, "Invalid characters")
                    : Yup.string().nullable(),
                comment: Yup.string().nullable(),
            });
        });

        return Yup.object().shape({
            group: Yup.string().required("Group is required"),
            translations: Yup.object().shape(translationShape),
        });
    }, [languages]);

    const handleAccordion = (langId) => (_, isExpanded) => {
        setExpanded(isExpanded ? langId : null);
    };

    const onSubmit = async (values) => {
        setLoading(true);

        const payload = {
            group: values.group,
            translations: Object.entries(values.translations)
                .filter(([_, val]) => val.name?.trim())
                .map(([langId, val]) => ({
                    lan_id: parseInt(langId),
                    name: val.name,
                    comment: val.comment
                }))
        };

        axios.post(`${REACT_APP.URL}/api/hdp/tts/adm/hotel/amenity`, payload, {
            headers: {
                "Content-Type": "application/json",
                Authorization: sessionStorage.getItem("token"),
                "client-id": REACT_APP.CLIENTID,
                "client-secret": REACT_APP.SECRET,
            },
        })
            .then(async () => {
                await getAmenities();
                setLoading(false);
                setOpen(false);
                toast.success("Amenity added successfully", { autoClose: 2000 });
            })
            .catch((err) => {
                setLoading(false);
                toast.error("An error occurred");
                console.error("Error submitting amenity:", err);
            });
    };

    return (
        <div>
            <Button variant="contained" onClick={() => {
                setOpen(true);
                if (languages.length > 0) {
                    setExpanded(requiredLangId);
                }
            }}>
                Add Amenity
            </Button>

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}
            >
                <Fade in={open}>
                    <Box sx={modalStyle}>
                        <Typography variant="h6" mb={2}>
                            Enter Amenity Information
                        </Typography>

                        <Formik
                            enableReinitialize
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={onSubmit}
                        >
                            {() => (
                                <Form autoComplete="off">
                                    {/* Group dropdown */}
                                    <Grid item xs={12} sx={{ mb: 2 }}>
                                        <Field
                                            fullWidth
                                            select
                                            name="group"
                                            component={TextField}
                                            label="Amenity Group"
                                        >
                                            {amenityGroups?.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid>

                                    {/* Translations accordion */}
                                    {languages.map((lang) => (
                                        <Accordion
                                            key={lang.id}
                                            expanded={expanded === lang.id}
                                            onChange={handleAccordion(lang.id)}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography>{lang.name}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Field
                                                            fullWidth
                                                            name={`translations.${lang.id}.name`}
                                                            label="Amenity Name"
                                                            component={TextField}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Field
                                                            fullWidth
                                                            name={`translations.${lang.id}.comment`}
                                                            label="Amenity Comment"
                                                            component={TextField}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}

                                    {/* Submit buttons */}
                                    <Box display="flex" justifyContent="space-between" mt={3}>
                                        <Button variant="contained" color="error" onClick={() => setOpen(false)}>
                                            Close
                                        </Button>
                                        {loading ? (
                                            <LoadingButton loading variant="contained" color="success">
                                                Submit
                                            </LoadingButton>
                                        ) : (
                                            <Button type="submit" variant="contained" color="success">
                                                Submit
                                            </Button>
                                        )}
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
}
